/**
 * Pure gate logic for the Post-App Wrap "SUBMIT APPLICATION" button.
 *
 * Kept free of MobX and React so it can be unit tested without a RootStore,
 * since the button itself is rendered inside a nested AgentAssistPanel prop.
 */

interface PreviouslySubmittedArgs {
    // True when the form was already submitted from the pre-approval page.
    submitted: boolean;
    // null = never submitted from the post-app-wrap page; otherwise the
    // Application Status value that was last submitted from that page.
    postAppWrapSubmitted: string | null;
    // The currently-selected Application Status.
    applicationStatus: string | number | boolean | undefined;
}

/**
 * Whether this form has already been submitted in a way that should block
 * resubmission: either from the pre-approval page, or from the post-app-wrap
 * page for the currently-selected Application Status.
 */
export const isPreviouslySubmitted = ({
    submitted,
    postAppWrapSubmitted,
    applicationStatus,
}: PreviouslySubmittedArgs): boolean =>
    submitted || (postAppWrapSubmitted != null && postAppWrapSubmitted === applicationStatus);

interface SubmitDisabledArgs {
    // At least one checklist section is missing a required field.
    isIncomplete: boolean;
    // A submission is currently in flight.
    isSubmitting: boolean;
    previouslySubmitted: boolean;
    // Agent explicitly chose to bypass the previously-submitted gate.
    overrideSubmitted: boolean;
}

/**
 * The override only bypasses the previously-submitted gate — incomplete
 * required fields and an in-flight submission still disable the button.
 */
export const isSubmitDisabled = ({
    isIncomplete,
    isSubmitting,
    previouslySubmitted,
    overrideSubmitted,
}: SubmitDisabledArgs): boolean => isIncomplete || isSubmitting || (previouslySubmitted && !overrideSubmitted);
